import React from "react";
import {
  Inbox, ShieldCheck, Search, Sparkles, ScrollText,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  FeatureCallout, AgentCard, ConversationThread,
  FlowDiagram, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogJosephAiFaxManager = () => (
  <RichBlogLayout
    category="Practice Efficiency"
    eyebrow="Fax Manager"
    title="Joseph: Every Inbound Fax Read, Routed, and Filed Before You Touch It"
    subtitle="EOBs, lab results, referrals, specialist reports — classified, extracted, and matched to the right chart, before anyone walks past the printer."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Prose>
      <p>
        It's 10:23 AM and the fax printer in the back hallway has fired
        seven times since coffee. There's a Delta EOB on top, a stack of
        lab results below it, a referral from Dr. Wong somewhere in the
        middle, and a prior auth approval that's been sitting since
        yesterday afternoon because nobody was sure whose patient it was.
        Someone has to stop what they're doing, sort the stack, figure out
        the patient on each page, rename the file, scan it into the right
        chart, and create the follow-up task.
      </p>
      <p>
        It's slow. It's repetitive. And it's the easiest place in the
        practice to silently drop something — a referral that never gets
        followed up, an EOB that never gets posted, a lab result that
        sits in the wrong folder for a week.
      </p>
      <p>
        Faxes never feel urgent. That's why they keep eating the day.
      </p>
    </Prose>

    <AgentCard
      name="Joseph"
      role="AI Fax Manager · Read, Classify, Route"
      initial="J"
      description="Joseph reads every inbound fax the moment it arrives, identifies the patient, classifies the document, extracts the clinical content that matters, and decides what should happen next — file, task, or route. No fax inbox to log into, no printer stack to sort."
      channels={["Inbound fax", "OCR + classify", "Chart match", "Auto-route"]}
    />

    <Prose>
      <h2>Why faxes still land on the front desk</h2>
      <p>
        A typical mid-sized practice receives 25–60 inbound faxes a day.
        Some days are calmer; some are 100+. Each fax takes about 3–5
        minutes of a person's time to handle properly: open, identify,
        rename, scan, attach, route, task. That's between 75 minutes and
        five hours a day spent on a workflow that produces no patient
        value.
      </p>
      <p>
        Multiply across a year and a practice has effectively dedicated a
        part-time role to opening faxes. And that role is, in most
        practices, distributed across whoever is closest to the printer
        when the page comes through.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 100, suffix: "%", label: "Of inbound faxes handled autonomously" },
        { value: 0, label: "Separate fax systems to log into" },
        { value: 4, suffix: " sec", label: "Median time to chart match" },
        { value: 6, label: "Document types classified instantly" },
      ]}
    />

    <Prose>
      <p>
        Joseph doesn't build a smarter fax inbox. He removes the inbox
        entirely. Faxes arrive, get read, get matched, get filed — and the
        team only sees the ones that genuinely need a human to make a call.
      </p>

      <h2>Storage was never the problem with faxes</h2>
      <p>
        Most fax tools store and search. Joseph reads and decides. He
        opens the document, identifies the patient, figures out what kind
        of document it is, extracts what matters, and chooses what should
        happen next. Filing a routine specialist report is one workflow.
        Routing a critical lab result to a provider is another. Joseph
        knows the difference.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Faxes, the way they work today"
      afterTitle="Faxes, with Joseph"
      beforeItems={[
        "Walk to the fax printer; sort the stack by hand",
        "Manually identify which patient each fax belongs to",
        "Rename, scan, and attach the file to the chart",
        "Create a task for the right teammate, hope it's followed up",
      ]}
      afterItems={[
        "Fax arrives, Joseph reads it within seconds",
        "Patient identified by name + DOB + cross-referenced details",
        "Classified — EOB, lab, referral, specialist, prior auth",
        "Filed cleanly or routed with context — automatically",
      ]}
    />

    <Prose>
      <h2>From inbound fax to filed in seconds</h2>
      <p>
        From the second a fax lands on the line, Joseph runs the same
        five-step pipeline. The whole thing takes seconds. The team is
        not in the loop unless Joseph genuinely can't make a confident
        decision — which is rare.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Reads the fax the moment it arrives",
          body:
            "OCR runs on every page. Joseph parses the text in the same way a human would skim — header, patient details, body, signatures.",
        },
        {
          title: "Identifies the patient",
          body:
            "Joseph extracts patient name, date of birth, and any other identifiers, then matches against your chart. Duplicate names get cross-checked against DOB, address, and provider history.",
        },
        {
          title: "Classifies the document type",
          body:
            "EOB, lab result, referral, specialist report, prior authorization, signed consent — Joseph categorizes the document instantly.",
        },
        {
          title: "Extracts the clinical content that matters",
          body:
            "Not just storage — Joseph pulls the actual important fields. The diagnosis on a referral. The procedure code on an EOB. The findings on a lab result.",
        },
        {
          title: "Decides what to do",
          body:
            "Some faxes need follow-up. Some need a task. Some just need to be filed. Joseph picks the right path and handles it — the team only steps in for genuine edge cases.",
        },
      ]}
    />

    <PullQuote>
      Joseph reads every inbound fax the moment it arrives. No one on your
      team has to open it, review it, or figure out what to do next.
    </PullQuote>

    <Prose>
      <h2>A morning of faxes, narrated</h2>
      <p>
        Here's what an hour of inbound fax traffic looks like when Joseph
        is running. None of these faxes hit a printer. None of them
        interrupted a teammate. Each one was read, classified, and routed
        in seconds — most of them filed silently, and only a couple
        bubbled up to a human.
      </p>
    </Prose>

    <ConversationThread
      title="Inbound fax stream · Tuesday morning"
      channel="Joseph · live trace"
      messages={[
        { from: "system", text: "9:14 AM · Inbound fax received · 3 pages" },
        { from: "agent", text: "Identified Maria L. · DOB match · Document type: EOB from Delta Dental. Total adjudicated $1,840. Auto-matched to claim #44182. Filed to chart. No action needed." },
        { from: "system", text: "9:21 AM · Inbound fax received · 2 pages" },
        { from: "agent", text: "Identified James T. · Document type: Specialist report from Dr. Wong (oral surgery). Recommendation: extract #19. Routed to Dr. Patel for treatment plan review with summary attached." },
        { from: "system", text: "9:34 AM · Inbound fax received · 1 page" },
        { from: "agent", text: "Identified Anna P. · Document type: Prior auth approval for D6010 (implant). Matched to pending request from 4/14. Closed and filed. Scheduling task created." },
        { from: "system", text: "9:47 AM · Inbound fax received · 4 pages" },
        { from: "agent", text: "Two candidate patients matched name 'D. Rivera.' Cross-checked DOB and address. Match confirmed: David R. (1982). Document type: Lab result. Filed to chart, flagged for hygienist review." },
      ]}
    />

    <Prose>
      <h2>The hard cases — duplicate names, blurry pages, missing data</h2>
      <p>
        Real fax traffic is messy. Two patients share a last name. A page
        is rotated. A header is cut off. Joseph handles these the same
        way a careful human would — by cross-referencing what he can,
        and asking for help only when the evidence isn't strong enough.
      </p>
      <p>
        When confidence drops below a threshold, Joseph stages the fax
        with a short summary of what he could and couldn't determine —
        and routes it to a teammate to confirm in seconds. No one is
        sorting through ten pages from scratch.
      </p>
    </Prose>

    <FlowDiagram
      title="From fax line to filed chart, end to end"
      nodes={[
        { label: "Fax arrives", sublabel: "Inbound line", icon: Inbox },
        { label: "Read + classify", sublabel: "OCR + intent", icon: Search },
        { label: "Match patient", sublabel: "Name + DOB", icon: ShieldCheck },
        { label: "Route or file", sublabel: "Decision", icon: ScrollText },
      ]}
    />

    <Prose>
      <h2>What stops happening at the printer</h2>
      <p>
        The walk to the printer. The "whose patient is this?" question
        across the front desk. The retyped file names. The orphaned PDFs
        sitting on a shared drive. The follow-up that never happened
        because the referral got buried under three days of other faxes.
      </p>
      <p>
        Faxes were always supposed to be a background workflow. Joseph
        finally makes them feel that way.
      </p>
    </Prose>

    <FeatureCallout
      stat="Zero touches"
      label="A workflow that completely disappears"
      description="Faxes get processed, filed, and routed before anyone has to touch them — no dedicated fax inbox, no manual upload, no task creation."
      icon={Sparkles}
    />

    <Prose>
      <h2>Operational impact across a month</h2>
      <p>
        At 30 minutes per day reclaimed — a conservative estimate for most
        practices — that's 10+ hours back to the team every month. But the
        bigger lift is the elimination of dropped faxes. Referrals that
        actually get followed up. Lab results that actually get reviewed.
        EOBs that actually get matched to the right claim.
      </p>
      <p>
        Faxes stop being a quietly leaky pipeline and become a clean,
        verified part of the chart.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Joseph turns a slow, manual, distraction-prone workflow into
        something the team genuinely never thinks about. Every fax read.
        Every fax matched. Every fax filed — before anyone has to touch it.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogJosephAiFaxManager;
